import { useAccount, useWriteContract, useReadContract, useWaitForTransactionReceipt } from 'wagmi'
import { useEffect } from 'react'
import { BaseVibeAbi } from '../utils/BaseVibeAbi'
import { CONTRACT_ADDRESS } from '../utils/constants'
import { useStreak } from './useStreak'

export enum VoteOption {
  Positive = 0, // 😊
  Negative = 1, // 😢
}

export interface VotingStats {
  positiveVotes: bigint
  negativeVotes: bigint
  totalVotes: bigint
  dateKey: bigint
}

export function useVoting() {
  const { address, isConnected } = useAccount()
  const { writeContract, data: hash, isPending, error, reset } = useWriteContract()
  const { isLoading: isConfirming, isSuccess: isConfirmed } = useWaitForTransactionReceipt({
    hash,
  })
  const { streak, updateStreak, refreshStreak } = useStreak(address)

  // Read if user has voted today
  const { data: hasVotedToday, refetch: refetchHasVoted } = useReadContract({
    address: CONTRACT_ADDRESS as `0x${string}`,
    abi: BaseVibeAbi,
    functionName: 'hasUserVotedToday',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && isConnected && CONTRACT_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // Read today's stats
  const { data: todayStats, refetch: refetchStats } = useReadContract({
    address: CONTRACT_ADDRESS as `0x${string}`,
    abi: BaseVibeAbi,
    functionName: 'getTodayStats',
    query: {
      enabled: isConnected && CONTRACT_ADDRESS !== '0x0000000000000000000000000000000000000000',
      refetchInterval: 5000, // Refetch every 5 seconds
    },
  })

  const vote = async (option: VoteOption) => {
    if (!isConnected || !address) {
      throw new Error('Wallet not connected')
    }

    // Reset previous transaction state before attempting a new transaction
    // This clears any old hash, error, or pending state that might interfere
    reset()

    try {
      writeContract({
        address: CONTRACT_ADDRESS as `0x${string}`,
        abi: BaseVibeAbi,
        functionName: 'vote',
        args: [option],
      })
    } catch (err) {
      console.error('Error initiating vote transaction:', err)
      throw err
    }
  }

  // Refetch stats and hasVotedToday after successful vote, and update streak
  useEffect(() => {
    if (isConfirmed) {
      // Update streak immediately when vote is confirmed
      updateStreak()
      
      // Add a small delay to ensure the transaction is processed on-chain
      const timer = setTimeout(() => {
        refetchStats()
        refetchHasVoted()
        // Refresh streak display
        refreshStreak()
      }, 2000) // Wait 2 seconds after confirmation

      return () => clearTimeout(timer)
    }
  }, [isConfirmed, refetchStats, refetchHasVoted, updateStreak, refreshStreak])

  const stats: VotingStats | null = todayStats
    ? {
        positiveVotes: todayStats[0],
        negativeVotes: todayStats[1],
        totalVotes: todayStats[2],
        dateKey: todayStats[3],
      }
    : null

  return {
    vote,
    hasVotedToday: hasVotedToday as boolean | undefined,
    stats,
    isVoting: isPending || isConfirming,
    isVoteSuccess: isConfirmed,
    error,
    refetchStats,
    streak,
    reset, // Expose reset so components can clear transaction state
  }
}



